import type { ReactNode } from 'react';
import { avatarHue, cx, initials } from '../lib/format';
import { PRIORITY_LABELS, PRODUCT_THEME, STATUS_META, CATEGORY_META } from '../lib/products';
import type { Category, Product, Status, User } from '../lib/types';
import { useTheme } from '../context/ThemeContext';

/* -------------------------------------------------------------------------- */
/* Theme toggle                                                                */
/* -------------------------------------------------------------------------- */

export function ThemeToggle({ className }: { className?: string }) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      onClick={toggleTheme}
      className={cx('btn-subtle p-2', className)}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      title={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
    >
      {isDark ? <SunIcon className="h-4 w-4" /> : <MoonIcon className="h-4 w-4" />}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* Avatar                                                                      */
/* -------------------------------------------------------------------------- */

export function Avatar({
  user,
  size = 28,
  ring = false,
}: {
  user: Pick<User, 'id' | 'name' | 'avatar'>;
  size?: number;
  ring?: boolean;
}) {
  const hue = avatarHue(user.id);
  return (
    <span
      className={cx(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white/95',
        ring && 'ring-2 ring-ink-900',
      )}
      style={{
        width: size,
        height: size,
        fontSize: Math.max(9, size * 0.38),
        background: `linear-gradient(135deg, hsl(${hue} 62% 46%), hsl(${(hue + 48) % 360} 66% 38%))`,
      }}
      title={user.name}
    >
      {user.avatar ? (
        <img src={user.avatar} alt="" className="h-full w-full rounded-full object-cover" />
      ) : (
        initials(user.name)
      )}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Chips                                                                       */
/* -------------------------------------------------------------------------- */

export function ProductChip({ product, className }: { product: Product; className?: string }) {
  const t = PRODUCT_THEME[product];
  return (
    <span className={cx('chip', t.chip, className)}>
      <span className={cx('h-1.5 w-1.5 rounded-full', t.dot)} />
      {t.label}
    </span>
  );
}

export function CategoryChip({ category }: { category: Category }) {
  const c = CATEGORY_META[category];
  return (
    <span className="chip chip-idle text-mist-400">
      <span aria-hidden="true">{c.icon}</span>
      {c.label}
    </span>
  );
}

export function StatusChip({ status }: { status: Status }) {
  const s = STATUS_META[status];
  return (
    <span className={cx('chip', s.chip)}>
      <span className={cx('h-1.5 w-1.5 rounded-full', s.dot)} />
      {s.label}
    </span>
  );
}

export function VisibilityChip({ visibility }: { visibility: 'public' | 'private' }) {
  if (visibility === 'public') {
    return (
      <span className="chip chip-idle text-mist-500">
        <LockOpenIcon /> Public
      </span>
    );
  }
  return (
    <span className="chip border-amber-400/30 bg-amber-400/10 text-amber-300">
      <LockIcon /> Private
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Priority                                                                    */
/* -------------------------------------------------------------------------- */

export function PriorityMeter({
  value,
  onChange,
  size = 'sm',
}: {
  value: number;
  onChange?: (v: number) => void;
  size?: 'sm' | 'lg';
}) {
  const interactive = Boolean(onChange);
  const dot = size === 'lg' ? 'h-2.5 w-7' : 'h-1.5 w-4';

  const bars = [1, 2, 3, 4, 5].map((n) => {
    const active = n <= value;
    const hue = value >= 4 ? 'bg-[#f472b6]' : value === 3 ? 'bg-[#f5a524]' : 'bg-[#4c8dff]';
    return (
      <span
        key={n}
        className={cx(
          'rounded-full transition-colors duration-150',
          dot,
          active ? hue : 'bg-[var(--tint-bg-strong)]',
        )}
      />
    );
  });

  if (!interactive) {
    return (
      <span
        className="inline-flex items-center gap-1"
        title={`Impact: ${PRIORITY_LABELS[value] ?? value} (${value}/5)`}
        aria-label={`Impact ${value} of 5`}
      >
        {bars}
      </span>
    );
  }

  return (
    <div
      className="inline-flex items-center gap-2"
      role="radiogroup"
      aria-label="Impact score"
    >
      <span className="inline-flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={`${PRIORITY_LABELS[n]} — ${n} of 5`}
            onClick={() => onChange?.(n)}
            className={cx(
              'rounded-full transition-all duration-150 hover:scale-y-150',
              dot,
              n <= value
                ? value >= 4
                  ? 'bg-[#f472b6]'
                  : value === 3
                    ? 'bg-[#f5a524]'
                    : 'bg-[#4c8dff]'
                : 'bg-[var(--tint-bg-strong)] hover:bg-[var(--hairline-strong)]',
            )}
          />
        ))}
      </span>
      <span className="text-xs font-medium text-mist-400">{PRIORITY_LABELS[value]}</span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* States                                                                      */
/* -------------------------------------------------------------------------- */

export function EmptyState({
  title,
  message,
  action,
  icon,
}: {
  title: string;
  message: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="surface flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="chip-idle mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border text-mist-400">
        {icon ?? <SearchIcon className="h-6 w-6" />}
      </div>
      <h3 className="text-h3 font-semibold text-mist-50">{title}</h3>
      <p className="mt-2 max-w-sm text-sm leading-relaxed text-mist-400 text-pretty">{message}</p>
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function ErrorState({
  title = 'Something went wrong',
  message,
  onRetry,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div
      role="alert"
      className="flex flex-col items-center justify-center rounded-card border border-rose-400/25 bg-rose-500/8 px-6 py-14 text-center"
    >
      <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-rose-400/30 bg-rose-500/10 text-rose-300">
        <AlertIcon className="h-6 w-6" />
      </div>
      <h3 className="text-h3 font-semibold text-mist-50">{title}</h3>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-rose-200/80 text-pretty">{message}</p>
      {onRetry && (
        <button type="button" onClick={onRetry} className="btn-ghost mt-6">
          <RefreshIcon className="h-4 w-4" /> Try again
        </button>
      )}
    </div>
  );
}

export function CardSkeleton() {
  return (
    <div className="surface flex h-full flex-col gap-4 p-5">
      <div className="flex gap-2">
        <div className="skeleton h-5 w-28 rounded-full" />
        <div className="skeleton h-5 w-20 rounded-full" />
      </div>
      <div className="skeleton h-5 w-4/5" />
      <div className="space-y-2">
        <div className="skeleton h-3 w-full" />
        <div className="skeleton h-3 w-11/12" />
        <div className="skeleton h-3 w-2/3" />
      </div>
      <div className="mt-auto flex items-center justify-between pt-2">
        <div className="skeleton h-7 w-7 rounded-full" />
        <div className="skeleton h-3 w-24" />
      </div>
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cx('animate-spin', className)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2.5" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/* Icons — inline so there is no icon-font or extra dependency                 */
/* -------------------------------------------------------------------------- */

type IconProps = { className?: string };
const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export const SearchIcon = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

export const AlertIcon = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
    <path d="M10.3 3.9 2.4 17.5A2 2 0 0 0 4.1 20.5h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
  </svg>
);

export const RefreshIcon = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
    <path d="M21 12a9 9 0 1 1-2.6-6.4" />
    <path d="M21 4v5h-5" />
  </svg>
);

export const PlusIcon = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const CloseIcon = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
    <path d="m6 6 12 12M18 6 6 18" />
  </svg>
);

export const LockIcon = () => (
  <svg viewBox="0 0 24 24" className="h-3 w-3" aria-hidden="true" {...stroke}>
    <rect x="4" y="10" width="16" height="11" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);

export const LockOpenIcon = () => (
  <svg viewBox="0 0 24 24" className="h-3 w-3" aria-hidden="true" {...stroke}>
    <rect x="4" y="10" width="16" height="11" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 7.5-1.9" />
  </svg>
);

export const ImageIcon = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <circle cx="8.5" cy="9.5" r="1.6" />
    <path d="m4 17 5-5 4 4 3-2 4 4" />
  </svg>
);

export const CommentIcon = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
    <path d="M20 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2Z" />
  </svg>
);

export const FilterIcon = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
    <path d="M3 5h18M6 12h12M10 19h4" />
  </svg>
);

export const CheckIcon = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
    <path d="m5 13 4 4L19 7" />
  </svg>
);

export const LogoutIcon = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
    <path d="M15 17v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v2" />
    <path d="M19 12H9m10 0-3-3m3 3-3 3" />
  </svg>
);

export const TrashIcon = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
    <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0v13a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V7" />
  </svg>
);

export const InboxIcon = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
    <path d="M4 13h4l2 3h4l2-3h4" />
    <path d="M5.5 5h13l2.5 8v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Z" />
  </svg>
);

export const SunIcon = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
    <circle cx="12" cy="12" r="4.2" />
    <path d="M12 2.5v2.4M12 19.1v2.4M4.9 4.9l1.7 1.7M17.4 17.4l1.7 1.7M2.5 12h2.4M19.1 12h2.4M4.9 19.1l1.7-1.7M17.4 6.6l1.7-1.7" />
  </svg>
);

export const MoonIcon = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
    <path d="M20.5 14.4A8.5 8.5 0 1 1 9.6 3.5a7 7 0 0 0 10.9 10.9Z" />
  </svg>
);

export const EyeIcon = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const EyeOffIcon = ({ className }: IconProps) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...stroke}>
    <path d="M3 3l18 18" />
    <path d="M10.6 5.2A9.9 9.9 0 0 1 12 5c6.4 0 10 7 10 7a17.9 17.9 0 0 1-3.5 4.5M6.6 6.6C4.1 8.3 2 12 2 12s3.6 7 10 7a9.7 9.7 0 0 0 4.4-1" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
);
